import type { QueryClient } from '@tanstack/react-query';
import { getTtsVoice, listTtsVoices, type TtsVoiceFilters } from '../../../features/scenarios/api';
import { NARRATION_PROVIDERS, providerFiltersVoicesOnServer, type NarrationProviderCode } from './constants';

/**
 * ナレーション音声プロバイダと voice_id の整合性を扱うユーティリティ。
 *
 * voice_id はプロバイダ固有 (ElevenLabs = 20 桁の英数 ID / Gemini = プリセット名 "Zephyr" 等) で、
 * 別プロバイダに投げると API 側で voice_not_found になる。シーンには `narration_voice_id` だけが
 * 保存されプロバイダは保存されないため、復元時は両プロバイダの声一覧から所属を逆引きする。
 */

// プロバイダ一覧の正本は constants.ts (速度レンジ等と同居)。ここは既存 import 互換の再エクスポート。
export { NARRATION_PROVIDERS, type NarrationProviderCode } from './constants';

export type VoiceListFilters = TtsVoiceFilters;

/** 空の絞り込みは null に正規化する (無条件一覧と同じ queryKey を共有するため) */
function normalizeVoiceFilters(filters?: VoiceListFilters): { gender?: string; age?: string; licensed?: true } | null {
  const out: { gender?: string; age?: string; licensed?: true } = {};
  if (filters?.gender) out.gender = filters.gender;
  if (filters?.age) out.age = filters.age;
  if (filters?.licensed) out.licensed = true;
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * useQuery / fetchQuery で共有する queryKey (useNarrationHandlers / SoundLogoGenerateModal と同じ)。
 * 絞り込み付き一覧は ['tts-voices', provider, {gender, age, licensed}]。無条件は従来どおり 2 要素。
 * 先頭 2 要素が共通なので、findVoiceInCache は同じプロバイダの全一覧 (絞り込み済み・1 件取得含む) を横断できる。
 */
export const ttsVoicesQueryKey = (provider: NarrationProviderCode, filters?: VoiceListFilters) => {
  const normalized = normalizeVoiceFilters(filters);
  return normalized ? (['tts-voices', provider, normalized] as const) : (['tts-voices', provider] as const);
};
/** ボイス 1 件取得 (getTtsVoice) の queryKey。data は voice オブジェクトまたは null。 */
export const ttsVoiceByIdQueryKey = (provider: NarrationProviderCode, voiceId: string) =>
  ['tts-voices', provider, 'by-id', voiceId] as const;
export const TTS_VOICES_STALE_TIME = 60 * 60 * 1000; // 1時間キャッシュ

type VoiceLike = { voice_id: string };

/** voice_id が一覧に含まれるか。一覧未ロード (undefined) は「含まれない」扱い。 */
export function isVoiceInList(voices: readonly VoiceLike[] | undefined, voiceId: string | undefined | null): boolean {
  if (!voiceId || !voices) return false;
  return voices.some((v) => v.voice_id === voiceId);
}

/**
 * プロバイダの全キャッシュ (無条件一覧・絞り込み済み一覧・1 件取得) から voice_id を探す。
 * API 側で絞るプロバイダ (Fish Audio) では「一覧」が固定の全集合ではなく、絞り込み経由で
 * 選んだ声は無条件一覧に無いことがある。選択中の声の妥当性判定・表示名の解決はこれを使う。
 */
export function findVoiceInCache<T extends VoiceLike = VoiceLike>(
  queryClient: QueryClient,
  provider: NarrationProviderCode,
  voiceId: string | undefined | null,
): T | undefined {
  if (!voiceId) return undefined;
  for (const [, data] of queryClient.getQueriesData<unknown>({ queryKey: ['tts-voices', provider] })) {
    if (Array.isArray(data)) {
      const hit = (data as T[]).find((v) => v?.voice_id === voiceId);
      if (hit) return hit;
    } else if (data && typeof data === 'object' && (data as VoiceLike).voice_id === voiceId) {
      return data as T;
    }
  }
  return undefined;
}

/**
 * voice_id が属するプロバイダを、各プロバイダの声一覧 (react-query キャッシュ経由) から逆引きする。
 * どの一覧にも無い (削除済み ElevenLabs ボイス等) 場合や一覧取得に失敗した場合は null。
 * 一覧はシナリオ編集画面と同じ queryKey で取得するので、以降の useQuery はキャッシュヒットする。
 *
 * API 側で絞るプロバイダ (Fish Audio) は無条件一覧が上位 100 件しか無いため、一覧に無ければ
 * 1 件取得 (getTtsVoice) で確かめる。見つかった声は by-id の queryKey にキャッシュされ、
 * findVoiceInCache 経由で妥当性判定・表示名に使える。
 */
export async function resolveNarrationProviderForVoice(
  queryClient: QueryClient,
  voiceId: string,
): Promise<NarrationProviderCode | null> {
  if (!voiceId) return null;
  // 全一覧・キャッシュで所属を確定してから、一覧外の声だけ個別取得する。
  // 並列の各一覧取得内で個別取得すると、Gemini の Orus まで ElevenLabs に送ってしまう。
  const listed = await Promise.all(
    NARRATION_PROVIDERS.map(async (provider) => {
      try {
        const voices = await queryClient.fetchQuery({
          queryKey: ttsVoicesQueryKey(provider),
          queryFn: () => listTtsVoices<VoiceLike>(provider),
          staleTime: TTS_VOICES_STALE_TIME,
        });
        return isVoiceInList(voices, voiceId) || findVoiceInCache(queryClient, provider, voiceId)
          ? provider : null;
      } catch {
        return findVoiceInCache(queryClient, provider, voiceId) ? provider : null;
      }
    }),
  );
  const known = listed.find((p): p is NarrationProviderCode => p !== null);
  if (known) return known;

  for (const provider of NARRATION_PROVIDERS) {
    if (!providerFiltersVoicesOnServer(provider)) continue;
    try {
      const voice = await queryClient.fetchQuery({
        queryKey: ttsVoiceByIdQueryKey(provider, voiceId),
        queryFn: () => getTtsVoice<VoiceLike>(provider, voiceId),
        staleTime: TTS_VOICES_STALE_TIME,
      });
      if (voice) return provider;
    } catch {
      // 他のプロバイダで復元できる可能性がある。
    }
  }
  return null;
}
